<template>
  <Modal
    v-if="showEdit"
    size="lg"
    confirmText="Spara ändringar"
    :loading="loading"
    cancelText="Avbryt"
    v-on:confirm="updateSubscription()"
    v-on:cancel="$emit('close')"
  >
    <div v-if="objectType == RENTALOBJECT_TYPES.APARTMENT">
      <div class="flex flex-col gap-4">
        <ObjectTypes
          v-if="!isStudentApartment"
          :objectType="objectType"
          type="RENTALOBJECT_TYPES.APARTMENT"
          v-model="filters.rentalObjectTypes"
          v-bind:activeFilters="filters"
          :type="RENTALOBJECT_TYPES.APARTMENT"
        />
        <TownDistricts v-model="filters.administrativeUnitAreas" />
        <div class="flex flex-col gap-2 mb-4">
          <label for="areaMin" class="inline-block uppercase text-[14px] tracking-[0.42px] font-bold">Antal rum</label>
          <div class="flex flex-row gap-2">
            <Rooms type="min" v-model="filters.minRooms" :max="filters.maxRooms"  />
            <Rooms type="max" v-model="filters.maxRooms" :min="filters.minRooms" />
          </div>
        </div>
        <div class="flex flex-col gap-2">
          <label for="roomCountMin" class="inline-block uppercase text-[14px] tracking-[0.42px] font-bold">
            Boendeyta (m
            <sup>2</sup>
            )
          </label>
          <div class="flex flex-row gap-2">
            <ObjectArea
              type="min"
              v-model="filters.minArea"
              :max="filters.maxArea"
              :activeType="objectType"
              class="flex-1"
            />
            <ObjectArea
              type="max"
              v-model="filters.maxArea"
              :activeType="objectType"
              :min="filters.minArea"
              class="flex-1"
            />
          </div>
        </div>
        <div class="flex flex-col gap-2">
          <label for="monthRent" class="inline-block uppercase text-[14px] tracking-[0.42px] font-bold mt-4 mb-2">Maxhyra</label>
          <Rent v-model="filters.maxRent" :activeType="objectType" />
        </div>
      </div>
    </div>

    <div v-if="objectType == RENTALOBJECT_TYPES.PARKING">
      <div>
        <ObjectTypes
          type="parking"
          :subscription="true"
          :objectType="objectType"
          v-model="filters.rentalObjectTypes"
          v-bind:activeFilters="filters"
          v-bind:allObjects="null"
        />
        <TownDistricts v-model="filters.administrativeUnitAreas" />

        <div>
          <label for="monthRent" class="inline-block uppercase text-[14px] tracking-[0.42px] font-bold mt-4 mb-2">Maxhyra</label>
          <Rent v-model="filters.maxRent" />
        </div>
      </div>
    </div>

    <div v-if="objectType == RENTALOBJECT_TYPES.PREMISE">
      <div>
        <div>
          <TownDistricts v-model="filters.administrativeUnitAreas" />

          <label for="roomCountMin" class="inline-block uppercase text-[14px] tracking-[0.42px] font-bold mt-4 mb-2">
            Storlek (m
            <sup>2</sup>
            )
          </label>
          <div class="flex gap-2 items-center">
            <ObjectArea type="min" v-model="filters.minArea" :max="filters.maxArea" /> -
            <ObjectArea type="max" v-model="filters.maxArea" :min="filters.minArea" />
          </div>
        </div>
        <div>
          <label for="monthRent" class="inline-block uppercase text-[14px] tracking-[0.42px] font-bold mt-4 mb-2">Maxhyra</label>
          <Rent v-model="filters.maxRent" />
        </div>
      </div>
    </div>

    <template #footer>
      <Button variant="danger" @click="deleteSubscription()">
        Ta bort bevakning
        <template #icon-after>
          <IconTrashCan />
        </template>
      </Button>
    </template>
  </Modal>
</template>

<script>
import Modal from '@/components/ui/modal';
import ObjectTypes from '@/components/search/filters_new/objecttypes';
import TownDistricts from '@/components/search/filters_new/towndistricts';
import Rent from '@/components/search/filters_new/rent';
import Rooms from '@/components/search/filters_new/rooms';
import ObjectArea from '@/components/search/filters_new/area';
import WaitingListType from '@/components/search/filters_new/waitinglisttype';
import Button from '@/components/ui/button';
import Loader from '@/components/ui/loader';
import SubscriptionMixin from '@/mixins/subscription';
import { RENTALOBJECT_TYPES } from '@/config/constants';
import { dataLayerPush } from '@/helpers/datalayer';
import IconTrashCan from '@/components/icons/IconTrashCan';

export default {
  mixins: [SubscriptionMixin],
  components: {
    Modal,
    Button,
    ObjectTypes,
    TownDistricts,
    Rent,
    IconTrashCan,
    Rooms,
    ObjectArea,
    Loader,
    WaitingListType,
  },
  props: {
    showEdit: {
      type: Boolean,
      required: true,
    },
    subscription: {
      type: Object,
      required: true,
    },
  },
  emits: ['close'],
  data() {
    return {
      RENTALOBJECT_TYPES,
      loading: false
    };
  },
  computed: {
    _subscription() {
      return { ...this.subscription };
    },
    isStudentApartment() {
      if (this._subscription && this._subscription.waitingListTypeCaption) {
        return !!this._subscription.waitingListTypeCaption.match('Prenumeration, student');
      }
      return false;
    },
  },
  created() {
    this.filters.rentalObjectTypes = this.subscription.rentalObjectTypes;
    this.filters.administrativeUnitAreas = this.subscription.administrativeUnitAreas;
    this.filters.maxRent = this.subscription.maxRent;
    this.filters.minArea = this.subscription.minArea;
    this.filters.maxArea = this.subscription.maxArea;
    this.filters.minRooms = this.subscription.minRooms;
    this.filters.maxRooms = this.subscription.maxRooms;
    this.filters.waitingListType = this.subscription.waitingListType;
    this.showLoader = false;
  },
  methods: {
    deleteSubscription() {
      this.loading = true;
      return SubscriptionMixin.methods.deleteSubscription.call(this).then(() => {
        this.loading = false;
        this.$emit('done');
        this.$emit('close');
        dataLayerPush({ event: 'deleteSetup' });
      });
    },
    updateSubscription() {
      this.loading = true;
      return SubscriptionMixin.methods.updateSubscription.call(this).then(() => {
        this.loading = false;
        this.$emit('done');
        this.$emit('close');
        dataLayerPush({ event: 'saveSetup' });
      });
    },
  },
};
</script>
